import React, { useState } from "react";
import { Box, Typography, Stack } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelIcon from "@mui/icons-material/Cancel";

const IraabActivity = ({
  currentActivity,
  submitted,
  iraabAnswers,
  selectedIraabWord,
  setSelectedIraabWord,
  setIraabAnswers,
  preview,
}) => {
  const [previewWordIndex, setPreviewWordIndex] = useState(null);

  // ⭐ NEW → store temporary haraka values per word
  const [previewAnswers, setPreviewAnswers] = useState({});

  if (!currentActivity?.Iraab?.Value) return null;

  const harakaatOptions = ["َ", "ُ", "ِ", "ْ"];
  const isSubmitted = submitted?.isSubmitted || false;

  // ---------------- WORD PROCESSING ----------------
  const allWords = currentActivity.Iraab.Value.split(" ").map((word) => {
    const harakaPattern = /[ًٌٍَُِْ]$/;
    const last = word.slice(-1);
    const hasHaraka = harakaPattern.test(last);
    const cleaned = hasHaraka ? word.slice(0, -1) : word;

    return { original: word, base: cleaned, hasHaraka };
  });

  // ---------------- CORRECTNESS ----------------
  let isCorrect = true;
  allWords.forEach((w, idx) => {
    if (!w.hasHaraka) return;
    const userWord = iraabAnswers[idx] || "";
    const correct = w.original;
    if (userWord.trim() !== correct.trim()) isCorrect = false;
  });

  const resultBgColor = isSubmitted
    ? isCorrect
      ? "#e8f5e9"
      : "#ffebee"
    : "transparent";

  // ---------------- WORD CLICK ----------------
  const handleWordClick = (index, hasHaraka) => {
    if (!hasHaraka) return;

    if (preview) {
      setPreviewWordIndex(index);
      return;
    }

    if (isSubmitted) return;
    setSelectedIraabWord(selectedIraabWord === index ? null : index);
  };

  // ---------------- HARAKA SELECTION ----------------
  const handleIraabSelection = (haraka) => {
    if (preview) {
      // ⭐ Save temporary per-word preview answer
      setPreviewAnswers((prev) => ({
        ...prev,
        [previewWordIndex]: allWords[previewWordIndex].base + haraka,
      }));

      setPreviewWordIndex(null); // close popup
      return;
    }

    // ⭐ Student mode
    if (selectedIraabWord !== null) {
      setIraabAnswers((prev) => ({
        ...prev,
        [selectedIraabWord]: allWords[selectedIraabWord].base + haraka,
      }));
    }
    setSelectedIraabWord(null);
  };

  const popupIndex = preview ? previewWordIndex : selectedIraabWord;

  return (
    <>
      <Typography
        variant="body2"
        sx={{
          m: 1.2,
          p: "4px 8px",
          fontStyle: "italic",
          backgroundColor: "#dfc4df",
          display: "inline-block",
          borderRadius: "4px",
        }}
      >
        Select the correct إعراب by choosing the correct haraka.
      </Typography>

      {/* WORDS */}
      <Box sx={{ direction: "rtl", textAlign: "right", mt: 1 }}>
        {allWords.map((w, index) => (
          <Box
            key={index}
            display="inline-block"
            mx={0.5}
            onClick={() => handleWordClick(index, w.hasHaraka)}
            sx={{
              cursor: w.hasHaraka ? "pointer" : "default",
              opacity: w.hasHaraka ? 1 : 0.6,
            }}
          >
            <Typography
              sx={{
                backgroundColor: "#fff",
                fontFamily: "Traditional Arabic, serif",
                fontSize: "1.6rem",
                px: 1,
                borderRadius: "6px",
                border:
                  popupIndex === index ? "2px solid #2196f3" : "1px solid #ddd",
              }}
            >
              {/* ⭐ Priority:
                  1. Preview temp answer
                  2. Student answer
                  3. Base word */}
              {preview && previewAnswers[index]
                ? previewAnswers[index]
                : iraabAnswers[index] || w.base}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* POPUP */}
      {popupIndex !== null && (
        <Box
          position="fixed"
          top={0}
          left={0}
          width="100vw"
          height="100vh"
          bgcolor="rgba(0,0,0,0.4)"
          zIndex={1400}
          display="flex"
          alignItems="center"
          justifyContent="center"
          onClick={() =>
            preview ? setPreviewWordIndex(null) : setSelectedIraabWord(null)
          }
        >
          <Box
            bgcolor="#fff"
            borderRadius={2}
            p={2}
            boxShadow={5}
            onClick={(e) => e.stopPropagation()}
          >
            <Stack spacing={1}>
              {harakaatOptions.map((haraka, idx) => (
                <Typography
                  key={idx}
                  onClick={() => handleIraabSelection(haraka)}
                  sx={{
                    backgroundColor: "#1976d2",
                    color: "#fff",
                    padding: "8px 16px",
                    borderRadius: 1,
                    cursor: "pointer",
                    fontSize: "1.4rem",
                    fontFamily: "Traditional Arabic, serif",
                    textAlign: "center",
                  }}
                >
                  {allWords[popupIndex].base + haraka}
                </Typography>
              ))}
            </Stack>
          </Box>
        </Box>
      )}

      {/* RESULT */}
      {isSubmitted && (
        <Box
          sx={{
            mt: 3,
            p: 2,
            borderRadius: 2,
            backgroundColor: resultBgColor,
          }}
        >
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            {isCorrect ? (
              <CheckCircleOutlineIcon sx={{ fontSize: 40, color: "green" }} />
            ) : (
              <CancelIcon sx={{ fontSize: 40, color: "red" }} />
            )}
          </Box>

          <Typography
            sx={{
              textAlign: "center",
              mt: 1,
              color: isCorrect ? "green" : "red",
              fontWeight: "bold",
            }}
          >
            {isCorrect ? "Correct!" : "Incorrect!"}
          </Typography>
        </Box>
      )}
    </>
  );
};

export default IraabActivity;
